import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Button, Stack, Table } from '@mantine/core';

import { useGetBoardUsers } from 'api/boards';
import { useGetOrganisation } from 'api/organisations';
import { PaginationReactRouter } from 'components/shared';
import { useSearchParams } from 'hooks';
import type { BoardType } from 'types';

import { MemberInvitationModal } from '../common';
import { MemberRow } from './MemberRow';

type Props = {
  board: BoardType;
};

export const Members = ({ board }: Props) => {
  const { organisationId } = useParams();
  if (!organisationId) throw new Error('Missing URL params');

  const [invitationModalOpened, setInvitationModalOpened] = useState(false);
  const { t } = useTranslation('organisations');

  const { data: organisation } = useGetOrganisation({
    pathParams: { organisationId },
  });
  const { isAdministrator } = organisation || { isAdministrator: false };

  const { getSearchParam, changeSearchParam } = useSearchParams(['page']);
  const page = Number(getSearchParam('page')) || 1;

  const { data: usersData } = useGetBoardUsers(
    {
      pathParams: { boardId: board.id },
      queryParams: { page, perPage: 20 },
    },
    { keepPreviousData: true },
  );
  const users = usersData?.data;
  const queryPagesCount = usersData?.meta.lastPage;
  const paginateList = queryPagesCount !== undefined && queryPagesCount > 1;

  return (
    <Stack spacing="xl">
      {isAdministrator && (
        <Box>
          <Button color="green" onClick={() => setInvitationModalOpened(true)}>
            {t('organisationView.boards.members.inviteButton')}
          </Button>
          <MemberInvitationModal
            opened={invitationModalOpened}
            onClose={() => setInvitationModalOpened(false)}
          />
        </Box>
      )}
      <Table
        sx={theme => ({
          '& tbody': {
            '& tr': {
              borderBottom: `1px solid ${theme.colors.gray[1]}`,
              '& td': {
                padding: theme.other.spacing(2),
                border: 'none',
                '&:last-child': {
                  textAlign: 'right',
                },
              },
            },
          },
        })}
      >
        <tbody>
          {users?.map(user => (
            <MemberRow
              key={user.id}
              user={user}
              isAdministrator={isAdministrator}
            />
          ))}
        </tbody>
      </Table>
      {paginateList && usersData?.meta && (
        <PaginationReactRouter meta={usersData?.meta} />
      )}
    </Stack>
  );
};
